import React, { useEffect, useState } from "react";
import { Lens } from "../aceternity/ui/lens";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faShoppingCart,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  addOrUpdateRatingThunk,
  addOrUpdateReviewThunk,
  addToCartThunk,
  addToWishlistThunk,
  fetchProductReviewsThunk,
} from "../thunks/thunks";

const Details = ({ item }) => {
  console.log("Item received in Details:", item);
  const {
    name = "N/A",
    price = "N/A",
    imageFileName = "",
    _id: id,
    averageRating: rating,
    ratings: ratingArray,
    description: description,
  } = item || {};
  const validRating = Math.max(1, Math.min(5, Math.round(rating)));
  const { reviews } = useSelector((state) => state.products);
  const dispatch = useDispatch();
  const [hovering, setHovering] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  // const [reviews, setReviews] = useState([
  //   { rating: 5, comment: "Great product, highly recommend!" },
  //   { rating: 4, comment: "Good quality, but sizing is a bit off." },
  //   { rating: 3, comment: "It's okay, but the fabric could be better." },
  // ]);
  const [newReview, setNewReview] = useState({ rating: 0, comment: "" });

  const handleSizeSelect = (size) => setSelectedSize(size);

  const handleAddToCart = () => {
    if (selectedSize) {
      dispatch(addToCartThunk({ productId: id, quantity: 1 }));
      toast.success("Item added to cart!");
    } else {
      toast.error("Please select a size to add to cart.");
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchProductReviewsThunk(id));
    }
  }, [id, dispatch]);

  const handleAddToWishlist = () => {
    dispatch(addToWishlistThunk(id));
    toast.success("Item added to wishlist!");
  };

  const handleAddReview = () => {
    if (newReview.rating > 0 && newReview.comment.trim() !== "") {
      // Send the rating and review to the backend
      dispatch(
        addOrUpdateReviewThunk({
          productId: id,
          comment: newReview.comment,
          stars: newReview.rating,
        })
      ).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          setNewReview({ rating: 0, comment: "" }); // Reset the review form

          // Update the rating in the backend (this will update the average rating)
          dispatch(
            addOrUpdateRatingThunk({
              productId: id,
              rating: newReview.rating, // Submit the rating along with review
            })
          ).then((ratingResult) => {
            if (ratingResult.meta.requestStatus === "fulfilled") {
              toast.success("Review and rating submitted successfully!");
            } else {
              toast.error(
                ratingResult.payload?.message || "Failed to update rating."
              );
            }
          });
        } else {
          toast.error(result.payload?.message || "Failed to submit review.");
        }
      });
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      toast.error("Please fill in both rating and comment.");
    }
  };

  const sizes = ["S", "M", "L", "XL", "XXL"];

  return (
    <>
      <div className="flex flex-col md:flex-row border-2 gap-10 p-5">
        {/* Images Section */}
        <div className="md:w-[60%] h-full lg:w-full">
          <Lens hovering={hovering} setHovering={setHovering}>
            <img
              src={imageFileName}
              alt={name}
              className="rounded-2xl w-full h-full"
            />
          </Lens>
        </div>

        {/* Product Details Section */}
        <div className="md:w-full lg:mt-16 p-6 bg-white rounded-lg shadow-lg">
          {/* Product Name */}
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{name}</h1>

          {/* Rating */}
          <div className="flex items-center mt-2">
            <div className="flex text-yellow-500">
              {[...Array(validRating)].map((_, i) => (
                <FontAwesomeIcon key={i} icon={faStar} />
              ))}
            </div>
            <span className="text-gray-600 ml-2">
              ({ratingArray.length} Reviews)
            </span>
          </div>

          {/* MRP & Discount */}
          <div className="mt-2">
            <p className="text-lg text-gray-700">
              MRP: $<span>{price}</span>
            </p>
          </div>

          {/* Select Size */}
          <div className="mt-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Select Size:
            </h3>
            <div className="flex space-x-2 mt-2">
              {sizes.map((size) => (
                <button
                  key={size}
                  onClick={() => handleSizeSelect(size)}
                  className={`px-3 py-1 rounded border ${
                    selectedSize === size
                      ? "bg-pink-500 text-white"
                      : "bg-white text-gray-800"
                  } hover:bg-black hover:text-white`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Add to Cart & Wishlist Buttons */}
          <div className="mt-6 flex space-x-4">
            <button
              className="flex items-center justify-center px-4 py-2 bg-pink-500 text-white rounded shadow-lg hover:bg-pink-600"
              onClick={handleAddToCart}
            >
              <FontAwesomeIcon icon={faShoppingCart} className="mr-2" />
              Add to Cart
            </button>
            <button
              onClick={handleAddToWishlist}
              className="flex items-center justify-center px-4 py-2 border border-pink-500 text-pink-500 rounded hover:bg-pink-100"
            >
              <FontAwesomeIcon icon={faHeart} className="mr-2" />
              Wishlist
            </button>
          </div>

          {/* Detailed Product Information */}
          <div className="mt-8 text-gray-700 space-y-4">
            <h2 className="text-xl font-semibold">Product Details</h2>
            <p>{description}</p>
          </div>
        </div>
      </div>

      <div className="border-2 border-gray-200 p-5 m-5 rounded-lg">
        {/* Reviews Section */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold">Customer Reviews</h2>
          <div className="space-y-4">
            {reviews.map((review, index) => (
              console.log("Review: ", review),
              <div key={index} className="border-b pb-4">
                <div className="flex items-center">
                  <div className="flex text-yellow-500">
                    {[...Array(review.rating)].map((_, i) => (
                      <FontAwesomeIcon key={i} icon={faStar} />
                    ))}
                  </div>
                  <span className="text-gray-600 ml-2">
                    ({review.rating} stars)
                  </span>
                </div>
                <p className="text-gray-700 mt-2">
                  <strong>{review.user}</strong> ({review.email})
                </p>
                <p className="text-gray-700 mt-2">{review.comment}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Add Review Form */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold">Add a Review</h2>
          <div className="flex items-center space-x-2 mt-4">
            <select
              className="px-3 py-1 border rounded"
              value={newReview.rating}
              onChange={(e) =>
                setNewReview({ ...newReview, rating: parseInt(e.target.value) })
              }
            >
              <option value={0}>Rating</option>
              {[1, 2, 3, 4, 5].map((rating) => (
                <option key={rating} value={rating}>
                  {rating} Star
                </option>
              ))}
            </select>
            <textarea
              className="p-2 border rounded w-full"
              placeholder="Write your review..."
              value={newReview.comment}
              onChange={(e) =>
                setNewReview({ ...newReview, comment: e.target.value })
              }
            />
          </div>
          <button
            onClick={handleAddReview}
            className="mt-4 px-6 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
          >
            Submit Review
          </button>
        </div>
      </div>
    </>
  );
};

export default Details;
